import { Avatar, Box, Button, ButtonGroup, Collapse, Divider, FormLabel, IconButton, IconButtonProps, Modal, Paper, styled, SxProps, TextField, Theme, Tooltip, Typography } from "@mui/material";
import { Dispatch, ReactNode, SetStateAction, useCallback, useState } from "react";
import produce from "immer";
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { Localized } from "@fluent/react";
import { useGridApiRef } from "@mui/x-data-grid";
import { UsersBrowser } from "../users/UsersBrowser";
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }) => ({
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: {
        transform: 'rotate(0deg)',
      },
    },
    {
      props: ({ expand }) => !!expand,
      style: {
        transform: 'rotate(180deg)',
      },
    },
  ],
}));

export interface PersonData {
    userName: string | null
    fullName: string
    position: string
    email: string
    companyName: string
}

export const defPersonData: PersonData = {
    userName: null,
    fullName: '',
    position: '',
    email: '',
    companyName: '',
}

export interface SimplePersonData {
    userName: string | null
    fullName: string
}

export const defSimplePersonData: SimplePersonData = {
    userName: null,
    fullName: '',
}

export interface SimplePersonalDataPickerProps {
    data: SimplePersonData
    setData: Dispatch<SetStateAction<SimplePersonData>>
    label: ReactNode
    styles?: SxProps<Theme>
    readonly?: boolean
}

export const SimplePersonalDataPicker = (props: SimplePersonalDataPickerProps) => {
    const { data, setData, label, styles, readonly } = props

    const usersGridRef = useGridApiRef()
    const [browse, setBrowse] = useState(false)

    const handleSelectUserClick = () => {
        usersGridRef.current.getSelectedRows().forEach(row => {
            setData({
                userName: row.userName,
                fullName: row.firstname + ' ' + row.lastName,
            })
        })
        setBrowse(false)
    }

    const isAppUser = data.userName !== null && data.userName !== ''

    return (
        <Box sx={styles}>
            {isAppUser ? (
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                        {label}
                    </Typography>
                    <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                            <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="bold" noWrap>{data.fullName}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{data.userName}</Typography>
                        </Box>
                        {!readonly && (
                            <Tooltip title={<Localized id="clear">Clear</Localized>}>
                                <IconButton size="small" onClick={() => setData(defSimplePersonData)} color="error">
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Paper>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        label={label}
                        value={data.fullName}
                        onChange={e => setData(prev => ({ ...prev, fullName: e.target.value }))}
                        fullWidth
                        size="small"
                    />
                    {!readonly && (
                        <Button onClick={() => setBrowse(true)} variant="contained" startIcon={<PersonSearchIcon />}>
                            <Localized id="select">Select</Localized>
                        </Button>
                    )}
                </Box>
            )}

            <Modal open={browse}>
                <Box sx={{
                    height: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 900,
                    bgcolor: 'background.paper',
                    border: '1px solid #000',
                    boxShadow: 24,
                    p: 2,
                }}>
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                        <UsersBrowser gridRef={usersGridRef} disabled={new Set()} />
                    </Box>
                    <ButtonGroup>
                        <Button onClick={handleSelectUserClick} variant="contained">
                            <Localized id="select">Select</Localized>
                        </Button>
                        <Button onClick={() => setBrowse(false)} variant="outlined">
                            <Localized id="cancel">Cancel</Localized>
                        </Button>
                    </ButtonGroup>
                </Box>
            </Modal>
        </Box>
    )
}

export interface PersonalDataPickerProps {
    data: PersonData
    setData: Dispatch<SetStateAction<PersonData>>
    label: ReactNode
    styles?: SxProps<Theme>
    readonly?: boolean
    expanded? : boolean
}
export const PersonalDataPicker = (props: PersonalDataPickerProps) => {
    const { data, setData, label, styles, readonly, expanded } = props

    const usersGridRef = useGridApiRef()

    const [browse, setBrowse] = useState(false)
    const [open, setOpen] = useState<boolean>(expanded ? expanded : data.userName === null || data.fullName === null)

    const handleBrowseClick = useCallback(() => {
        setBrowse(true)
    }, [])
    const handleCancelClick = useCallback(() => {
        setBrowse(false)
    }, [])
    const handleSelectUserClick = () => {
        usersGridRef.current.getSelectedRows().forEach(row => {
            setData(produce(draft => {
                draft.userName = row.userName
                draft.fullName = row.firstname + ' ' + row.lastName
                draft.position = row.position
                draft.email = row.email
                draft.companyName = row.companyName
            }))
        })
        setBrowse(false)
    }
    const isAppUser = data.userName !== null && data.userName !== ''
    const isInputReadonly = isAppUser || readonly

    return <Box sx={styles}>
        <Box sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
        }}>
            <FormLabel sx={{ display: 'inline-block' }}>{label}: {data.fullName}</FormLabel>
            <ExpandMore sx={{ flexGrow: 0, mr: 'auto', ml: 0 }} size="small" expand={open} onClick={() => setOpen(!open)}>
                <ExpandMoreIcon fontSize="small" />
            </ExpandMore>
        </Box>
        <Collapse sx={{
            ['& .MuiCollapse-wrapperInner'] : {
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
            }
        }} in={open}>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <TextField sx={{ flexGrow: 1 }}
                    label={<Localized id="name">Name</Localized>}
                    value={data.fullName}
                    onChange={e => setData(produce(draft => { draft.fullName = e.target.value }))}
                    inputProps={{
                        'readonly': isInputReadonly ? 'true' : undefined
                    }}
                    fullWidth
                />
                {isAppUser && !readonly && <Tooltip title={<Localized id="clear">Clear</Localized>}>
                    <IconButton onClick={() => setData(defPersonData)} color='error'><ClearIcon /></IconButton>
                </Tooltip>}
                {!readonly && <Button onClick={handleBrowseClick} variant='contained' startIcon={<PersonSearchIcon />}>
                    <Localized id="select">Select</Localized>
                </Button>}
            </Box>
            <TextField
                label={<Localized id="email">Email</Localized>}
                value={data.email}
                onChange={e => setData(produce(draft => { draft.email = e.target.value }))}
                inputProps={{
                    'readonly': isInputReadonly ? 'true' : undefined
                }}
                fullWidth
            />
            <TextField
                label={<Localized id="company">Company</Localized>}
                value={data.companyName}
                onChange={e => setData(produce(draft => { draft.companyName = e.target.value }))}
                inputProps={{
                    'readonly': isInputReadonly ? 'true' : undefined
                }}
                fullWidth
            />
            <TextField
                label={<Localized id="position">Position</Localized>}
                value={data.position}
                onChange={e => setData(produce(draft => { draft.position = e.target.value }))}
                inputProps={{
                    'readonly': isInputReadonly ? 'true' : undefined
                }}
                fullWidth
            />
        </Collapse>
        <Modal open={browse}>
            <Box sx={{
                height: '80vh',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 900,
                bgcolor: 'background.paper',
                border: '1px solid #000',
                boxShadow: 24,
                p: 2,
            }}>
                <Box sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                }}>
                    <UsersBrowser gridRef={usersGridRef} disabled={new Set()} />
                </Box>
                <ButtonGroup>
                    <Button onClick={handleSelectUserClick} variant='contained'><Localized id="select">Select</Localized></Button>
                    <Button onClick={handleCancelClick} variant='outlined'><Localized id='cancel'>Cancel</Localized></Button>
                </ButtonGroup>
            </Box>
        </Modal>
    </Box>
}
